import { FC, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Avatar, AvatarGroup } from '@mui/material';

import styles from './TripCard.module.scss';
import { DateIcon, DeleteIcon, EditIcon, MarkerPinIcon, MergeIcon } from '../../../../../components/Icons';
import { Trip } from '../../../../../types/trip.types';
import { formatTripDuration, formatDateRangeWithYear } from '../../../../../utils/dateUtils';
import { buildAvatarUrl, generateInitials } from '../../../../../utils/avatarUtils';
import MergeTripsModal from '../MergeTripsModal/MergeTripsModal';
import ThreeDotsMenu, { MenuItemConfig } from '../../../../../components/ThreeDotsMenu/ThreeDotsMenu';
import { useDeleteTripCustomCoverMutation } from '../../../../../store/services/tripsApi';
import TripCoverUploadModal from '../TripCoverUploadModal/TripCoverUploadModal';
import tripMockDetailsFallback from '../../../../../assets/images/TripMockDetails.jpg';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';

type PendingTripConfirm = null | 'trip' | 'cover';

interface TripCardProps {
  trip: Trip;
  variant: 'full' | 'compact';
  isActive: boolean;
  onDelete?: (tripId: number) => void | Promise<void>;
  onMergeSuccess?: () => void;
}

const TripCard: FC<TripCardProps> = ({ trip, variant, isActive, onDelete, onMergeSuccess }) => {
  const navigate = useNavigate();
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showTripCoverModal, setShowTripCoverModal] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingTripConfirm>(null);
  const [isDeletingTrip, setIsDeletingTrip] = useState(false);
  const [deleteTripCustomCover, { isLoading: isDeletingCover }] = useDeleteTripCustomCoverMutation();
  const tripCoverUrl = trip.custom_cover;

  const getDestinationText = () => {
    if (trip.destination_countries && trip.destination_countries.length > 0) {
      return trip.destination_countries.map(country => country.name).join(', ');
    }
    return 'Unknown destination';
  };

  const renderAvatars = () => {
    const buddies = trip.buddies || [];

    if (buddies.length === 0) {
      return null;
    }

    return (
      <AvatarGroup
        max={4}
        className={styles.avatarGroup}
      >
        {buddies.map(buddy => {
          const avatarUrl = buildAvatarUrl(buddy.profile_picture);
          const hasIdentity = Boolean(
            buddy.first_name?.trim() || buddy.last_name?.trim() || buddy.email?.trim() || buddy.username?.trim()
          );
          const initials = hasIdentity
            ? generateInitials(buddy.first_name, buddy.last_name, buddy.email, buddy.username)
            : `U${String(buddy.id).slice(-1)}`;

          return (
            <Avatar
              key={buddy.id}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: avatarUrl ? 'transparent' : '#e3f2fd',
                color: avatarUrl ? 'transparent' : '#0098f0',
                fontSize: '10px',
                fontWeight: 600,
              }}
              src={avatarUrl || undefined}
              slotProps={{
                img: {
                  style: {
                    objectFit: 'cover',
                    objectPosition: 'center',
                  },
                },
              }}
            >
              {!avatarUrl && initials}
            </Avatar>
          );
        })}
      </AvatarGroup>
    );
  };

  const handleCardClick = () => {
    navigate(`/trips/${trip.id}`);
  };

  const handleMergeModalClose = () => {
    setShowMergeModal(false);
  };

  const handleMergeSuccess = () => {
    onMergeSuccess?.();
    setShowMergeModal(false);
  };

  const handleTripCoverModalClose = () => {
    setShowTripCoverModal(false);
  };

  const performCoverDelete = async () => {
    try {
      await deleteTripCustomCover(trip.id).unwrap();
      setPendingConfirm(null);
    } catch (error) {
      console.error('Failed to delete trip cover:', error);
    }
  };

  const performTripDelete = async () => {
    if (!onDelete) return;
    setIsDeletingTrip(true);
    try {
      await Promise.resolve(onDelete(trip.id));
      setPendingConfirm(null);
    } finally {
      setIsDeletingTrip(false);
    }
  };

  const isCoverActionLoading = isDeletingCover;

  const menuItems: MenuItemConfig[] = [
    {
      id: 'trip-cover-upload',
      label: tripCoverUrl ? 'Change cover' : 'Upload cover',
      icon: <EditIcon />,
      disabled: isCoverActionLoading,
      onClick: event => {
        event.stopPropagation();
        setShowTripCoverModal(true);
      },
    },
    ...(tripCoverUrl
      ? [
          {
            id: 'trip-cover-delete',
            label: 'Delete cover',
            icon: <DeleteIcon />,
            variant: 'danger' as const,
            disabled: isCoverActionLoading,
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              setPendingConfirm('cover');
            },
          },
        ]
      : []),
    {
      id: 'merge',
      label: 'Merge to another trip',
      icon: <MergeIcon />,
      onClick: event => {
        event.stopPropagation();
        setShowMergeModal(true);
      },
    },
    ...(trip.is_user_owner
      ? [
          {
            id: 'delete',
            label: 'Delete Trip',
            icon: <DeleteIcon />,
            variant: 'danger' as const,
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              setPendingConfirm('trip');
            },
          },
        ]
      : []),
  ];

  const getTitleVariant = () => (variant === 'full' ? 'h9Semibold' : 'h10Semibold');
  const getTextVariant = () => (variant === 'full' ? 'h11' : 'h12');
  const getDurationVariant = () => (variant === 'full' ? 'h11' : 'h12');

  return (
    <>
      <Box
        className={`${styles.tripCard} ${styles[variant]} ${isActive ? styles.active : ''}`}
        onClick={handleCardClick}
      >
        {variant === 'full' && (
          <Box className={styles.tripImageContainer}>
            <Box
              className={styles.tripImage}
              sx={{
                ...(tripCoverUrl
                  ? {
                      backgroundImage: `url(${tripCoverUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }
                  : {
                      backgroundImage: `url(${tripMockDetailsFallback})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }),
              }}
            >
              <Box
                className={styles.menuWrapper}
                onClick={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
              >
                <ThreeDotsMenu
                  menuItems={menuItems}
                  iconSize={28}
                  iconClassName={styles.menuIconOnImage}
                />
              </Box>
            </Box>
          </Box>
        )}

        {variant === 'compact' && (
          <ThreeDotsMenu
            menuItems={menuItems}
            iconSize={24}
            rootClassName={styles.compactMenuButton}
          />
        )}

        <Box className={styles.tripContent}>
          <Box className={styles.tripHeader}>
            <Typography
              variant={getTitleVariant()}
              className={styles.tripTitle}
            >
              {trip.name}
            </Typography>
            <Typography
              variant={getDurationVariant()}
              className={styles.tripDuration}
            >
              • {formatTripDuration(trip.start_date, trip.end_date)}
            </Typography>
            {isActive && (
              <Box className={styles.activeChip}>
                <Typography variant="h12Semibold">Active</Typography>
              </Box>
            )}
          </Box>

          <Box className={styles.tripDetails}>
            <Box className={styles.tripDate}>
              <DateIcon size={20} />
              <Typography variant={getTextVariant()}>
                {formatDateRangeWithYear(trip.start_date, trip.end_date)}
              </Typography>
            </Box>

            <Box className={styles.tripLocation}>
              <Box className={styles.tripMarker}>
                <MarkerPinIcon size={20} />
                <Typography variant={getTextVariant()}>{getDestinationText()}</Typography>
              </Box>
              {renderAvatars()}
            </Box>
          </Box>
        </Box>
      </Box>
      <MergeTripsModal
        open={showMergeModal}
        onClose={handleMergeModalClose}
        selectedTrip={trip}
        onSuccess={handleMergeSuccess}
      />
      <TripCoverUploadModal
        open={showTripCoverModal}
        onClose={handleTripCoverModalClose}
        tripId={trip.id}
      />
      <ConfirmActionModal
        open={pendingConfirm !== null}
        onClose={() => setPendingConfirm(null)}
        onConfirm={pendingConfirm === 'trip' ? performTripDelete : performCoverDelete}
        description={
          pendingConfirm === 'trip'
            ? 'Do you really want to delete this trip?'
            : 'Do you really want to delete the cover image?'
        }
        confirmLabel={pendingConfirm === 'trip' ? 'Delete Trip' : 'Delete'}
        isConfirming={pendingConfirm === 'trip' ? isDeletingTrip : isDeletingCover}
      />
    </>
  );
};

export default TripCard;
